// Web Crypto-based URL param encryption.
//
// Wraps a small JSON payload in AES-GCM keyed by a PBKDF2-derived key from
// the tenant's url_param_secret (delivered via /auth/me, held in memory only
// on authStore.urlParamSecret per Decision 3.3). The output is a single
// opaque base64url string used as the value of the ?d= URL parameter.
//
// Threat model: URL obfuscation + tamper detection.
//   - HTTPS handles transport secrecy.
//   - Server-side `WHERE company_id = ?` is the real authorisation
//     boundary (CLIENT-DECISION-004).
//   - This layer prevents staff from peeking at or rewriting URL params
//     to view/modify other drivers' rosters in their own company. AES-GCM's
//     auth tag guarantees a tampered ciphertext fails decryption rather
//     than silently producing garbage.
//
// Salt is a fixed application label. Different tenants have different
// `url_param_secret` values so derived keys still differ per tenant. Salt
// reuse across tenants is acceptable because the secret rotates per
// tenant and the threat model does not require offline-attack resistance
// (the secret never leaves the server / authenticated browser session).

const PBKDF2_ITERATIONS = 100_000;
const PBKDF2_SALT = new TextEncoder().encode('tricivo-url-params-v1');
const AES_KEY_BITS = 256;
const IV_BYTES = 12;

export class UrlParamError extends Error {
  constructor(message: string = 'Invalid or tampered URL') {
    super(message);
    this.name = 'UrlParamError';
  }
}

// Cache derived keys per secret — PBKDF2 with 100k iterations is ~30-100ms
// and we don't want every driver-switch / week-nav to pay that cost.
const keyCache = new Map<string, Promise<CryptoKey>>();

function deriveKey(secret: string): Promise<CryptoKey> {
  let cached = keyCache.get(secret);
  if (cached) return cached;
  cached = (async () => {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secret),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: PBKDF2_SALT,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-GCM', length: AES_KEY_BITS },
      false,
      ['encrypt', 'decrypt'],
    );
  })();
  keyCache.set(secret, cached);
  return cached;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(encoded: string): Uint8Array {
  const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    throw new UrlParamError();
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

export async function encryptParams(
  params: Record<string, string | number>,
  secret: string,
): Promise<string> {
  if (!secret || typeof secret !== 'string') {
    throw new UrlParamError('Cannot encrypt without url_param_secret');
  }
  if (!params || typeof params !== 'object') {
    throw new UrlParamError('Params must be an object');
  }
  const key = await deriveKey(secret);
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(params));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext),
  );
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  return toBase64Url(combined);
}

export async function decryptParams(
  encoded: string,
  secret: string,
): Promise<Record<string, string | number>> {
  if (!encoded || typeof encoded !== 'string') {
    throw new UrlParamError();
  }
  if (!secret || typeof secret !== 'string') {
    throw new UrlParamError('Cannot decrypt without url_param_secret');
  }
  const combined = fromBase64Url(encoded);
  if (combined.length <= IV_BYTES) {
    throw new UrlParamError();
  }
  const iv = combined.slice(0, IV_BYTES);
  const ciphertext = combined.slice(IV_BYTES);
  let plaintext: ArrayBuffer;
  try {
    const key = await deriveKey(secret);
    plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  } catch {
    throw new UrlParamError();
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    throw new UrlParamError();
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new UrlParamError();
  }
  // Validate value shapes — only string/number values survive.
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof v === 'string') {
      out[k] = v;
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      out[k] = v;
    } else {
      throw new UrlParamError();
    }
  }
  return out;
}
